// src/features/goal/components/GoalTaskList.tsx
// Goal Detail 頁的 Task 列表元件
//
// 功能：
// - All / Open / Closed 三個 Tab 篩選
// - Checkbox 直接切換 Task 狀態
// - 點擊 Task 標題開啟 TaskDetail
// - AI 拆解按鈕（由父層控制顯示/隱藏 GoalBreakdownPanel）

import React, { useState, useRef, useMemo } from 'react';
import { Sparkles, Loader2, Square, CheckSquare, Plus } from 'lucide-react';
import { Task, TaskStatus, isVisibleTask } from '../../../types/task';
import TaskDetail from '../../../components/tasks/TaskDetail';
import { eventBus, EVENTS } from '../../../utils/eventBus';

type TabFilter = 'all' | 'open' | 'closed';

interface GoalTaskListProps {
  tasks: Task[];
  tasksLoading: boolean;
  onToggleStatus: (task: Task) => void;
  onRefresh: () => void;
  onBreakdown: () => void;
  onAddTask: (title: string) => void;
  breakdownActive?: boolean;
}

const GoalTaskList: React.FC<GoalTaskListProps> = ({
  tasks,
  tasksLoading,
  onToggleStatus,
  onRefresh,
  onBreakdown,
  onAddTask,
  breakdownActive = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // 排除已 Drop 的 Task
  const visibleTasks = useMemo(
    () => tasks.filter(isVisibleTask),
    [tasks]
  );

  // 篩選 Tasks
  const filteredTasks = useMemo(() => {
    switch (activeTab) {
      case 'open':
        return visibleTasks.filter(t => t.status === 'OPEN');
      case 'closed':
        return visibleTasks.filter(t => t.status === 'CLOSED');
      default:
        return visibleTasks;
    }
  }, [visibleTasks, activeTab]);

  // Tab 計數
  const counts = useMemo(() => ({
    all: visibleTasks.length,
    open: visibleTasks.filter(t => t.status === 'OPEN').length,
    closed: visibleTasks.filter(t => t.status === 'CLOSED').length,
  }), [visibleTasks]);

  const tabs: { key: TabFilter; label: string }[] = [
    { key: 'all', label: `All (${counts.all})` },
    { key: 'open', label: `Open (${counts.open})` },
    { key: 'closed', label: `Closed (${counts.closed})` },
  ];

  function openAddInput() {
    setIsAdding(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function submitInput() {
    const title = inputValue.trim();
    if (title) {
      onAddTask(title);
      setInputValue('');
      // 留著 input 繼續新增（業界慣例）
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      submitInput();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setInputValue('');
    }
  }

  function handleBlur() {
    submitInput();
    setIsAdding(false);
    setInputValue('');
  }

  return (
    <div>
      {/* Tab 列 + AI 拆解按鈕 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                ${activeTab === tab.key
                  ? 'bg-sky-500 text-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              aria-label={`Filter ${tab.key} tasks`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => { eventBus.publish(EVENTS.UI.GOAL_SUGGEST_CLICKED); onBreakdown(); }}
          data-lesson-anchor="goal-suggest"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            breakdownActive
              ? 'bg-teal-50 text-teal-700'
              : 'text-teal-600 hover:bg-teal-50'
          }`}
          aria-label="Break down into tasks"
        >
          <Sparkles size={16} />
          <span className="hidden sm:inline">Suggest</span>
        </button>
      </div>

      {/* Task 列表 */}
      {tasksLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={20} className="animate-spin text-gray-400" />
        </div>
      ) : filteredTasks.length === 0 && !isAdding ? (
        <div className="text-center py-8">
          {activeTab === 'open' && visibleTasks.length > 0 ? (
            <p className="text-sm text-gray-400">All caught up!</p>
          ) : (
            <>
              <p className="text-sm text-gray-400 mb-3">No tasks yet</p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={openAddInput}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium
                             text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Add task manually"
                >
                  <Plus size={16} />
                  Add task
                </button>
                {!breakdownActive && (
                  <button
                    onClick={onBreakdown}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium
                               text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
                    aria-label="Suggest tasks"
                  >
                    <Sparkles size={16} />
                    Suggest
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {filteredTasks.map(task => (
            <div
              key={task.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              {/* Checkbox：樂觀 task 同步中，禁止操作 */}
              <button
                onClick={() => onToggleStatus(task)}
                disabled={task.id.startsWith('optimistic-')}
                className="flex-shrink-0 text-gray-400 hover:text-teal-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label={task.status === 'OPEN' ? `Complete ${task.title}` : `Reopen ${task.title}`}
              >
                {task.status === 'CLOSED' ? (
                  <CheckSquare size={20} className="text-green-500" />
                ) : (
                  <Square size={20} />
                )}
              </button>

              {/* Task 標題：樂觀 task 同步中，顯示 spinner 取代點擊 */}
              {task.id.startsWith('optimistic-') ? (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="text-sm text-gray-400 truncate">{task.title}</span>
                  <Loader2 size={13} className="flex-shrink-0 animate-spin text-gray-300" />
                </div>
              ) : (
                <button
                  onClick={() => setSelectedTaskId(task.id)}
                  className={`flex-1 text-left text-sm truncate transition-colors
                    ${task.status === 'CLOSED'
                      ? 'text-gray-400 line-through'
                      : 'text-gray-900 hover:text-teal-600'
                    }`}
                  aria-label={`View ${task.title}`}
                >
                  {task.title}
                </button>
              )}

              {/* 預估時間 badge */}
              {task.estimatedTime && (
                <span className="flex-shrink-0 text-xs text-gray-400">
                  {task.estimatedTime >= 60
                    ? `${Math.floor(task.estimatedTime / 60)}h`
                    : `${task.estimatedTime}m`}
                </span>
              )}
            </div>
          ))}

          {/* Inline 新增輸入 */}
          {isAdding ? (
            <div className="flex items-center gap-3 px-4 py-3">
              <Plus size={20} className="flex-shrink-0 text-gray-300" />
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder="Task title..."
                maxLength={30}
                className="flex-1 text-sm text-gray-900 placeholder-gray-400 bg-transparent outline-none"
                aria-label="New task title"
              />
            </div>
          ) : (
            <button
              onClick={openAddInput}
              className="flex items-center gap-3 px-4 py-3 w-full text-left
                         text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
              aria-label="Add task"
            >
              <Plus size={20} className="flex-shrink-0" />
              <span className="text-sm">Add task</span>
            </button>
          )}
        </div>
      )}

      {/* TaskDetail */}
      {selectedTaskId && (
        <TaskDetail
          isOpen={!!selectedTaskId}
          taskId={selectedTaskId}
          initialTask={tasks.find(t => t.id === selectedTaskId)}
          onClose={() => {
            setSelectedTaskId(null);
            onRefresh();
          }}
          onStatusChange={(taskId: string, newStatus: TaskStatus) => {
            // newStatus 與 onToggleStatus 計算結果一致（Task 只有 OPEN/CLOSED 兩態）
            // onToggleStatus 會對 task.status 取反，故傳入原始 task（舊狀態）
            const task = tasks.find(t => t.id === taskId);
            if (task && task.status !== newStatus) onToggleStatus(task);
          }}
        />
      )}
    </div>
  );
};

export default GoalTaskList;
